import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import { Link as LinkIcon, LinkOff as LinkOffIcon, Info as InfoIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../../../config/api';
import { isKnowledgeTool } from '../../../utils/toolVisibility';
import useIsMobile from '../useIsMobile';
import './AssistantSettingsTheme.css';

const ToolsSettings = ({ assistant, onUpdate }) => {
  const isMobile = useIsMobile();
  // Tracks WHICH tool is in flight, not merely that something is. A single
  // boolean put every row into the spinner/disabled state at once, so toggling
  // one tool looked like the whole list was being changed.
  const [pendingToolId, setPendingToolId] = useState(null);
  const [allTools, setAllTools] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTools = async () => {
      if (!assistant?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/api/${assistant.id}/tools`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch tools');
        }
        
        const data = await response.json();
        setAllTools(data);
      } catch (err) {
        setError('Failed to load tools. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, [assistant]);
  
  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleToggleToolLink = async (tool, shouldLink) => {
    try {
      setPendingToolId(tool.id);
      setError(null);
      const token = localStorage.getItem('access_token');

      const endpoint = shouldLink
        ? `${API_BASE_URL}/api/${assistant.id}/tools/link`
        : `${API_BASE_URL}/api/${assistant.id}/tools/unlink`;

      const method = shouldLink ? 'POST' : 'DELETE';
      const payload = { tool_id: tool.id, tool_name: tool.name };

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update tool status');
      }

      // Re-fetch the updated tools list
      const refreshResponse = await fetch(`${API_BASE_URL}/api/${assistant.id}/tools`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      if (refreshResponse.ok) {
        const updatedTools = await refreshResponse.json();
        setAllTools(updatedTools);
      }

      showSuccessMessage(shouldLink ? 'Tool linked successfully' : 'Tool unlinked successfully');
      
      if (onUpdate) onUpdate();

    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error(err);
    } finally {
      setPendingToolId(null);
    }
  };

  const visibleTools = allTools.filter(tool => !isKnowledgeTool(tool));
  const attachedTools = visibleTools.filter(tool => tool.is_linked === true);
  const unattachedTools = visibleTools.filter(tool => tool.is_linked === false || tool.is_linked === null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // One row per tool: name + badge, description beneath, action on the right.
  // Replaces the MUI ListItem/secondaryAction combination, whose absolutely
  // positioned action overlapped the tool name on narrow widths and needed a
  // separate mobile branch to work around it. A plain flex row needs neither.
  const renderToolRow = (tool, isLinked) => {
    const isPending = pendingToolId === tool.id;
    return (
      <li className="assistantToolRow" key={tool.id}>
        <div className="assistantToolMain">
          <div className="assistantToolTitleRow">
            <span className="assistantToolName" title={tool.name}>{tool.name}</span>
            <span className={`assistantToolBadge${tool.is_custom ? ' custom' : ''}`}>
              {tool.is_custom ? 'Custom' : 'Built-in'}
            </span>
          </div>
          {!isMobile && tool.description && (
            <p className="assistantToolDescription">{tool.description}</p>
          )}
        </div>

        <Tooltip title={isLinked ? 'Unlink tool' : 'Link tool'}>
          {/* span wrapper so the Tooltip still works while the button is
              disabled, MUI cannot attach a listener to a disabled button. */}
          <span className="assistantToolActionWrap">
            <IconButton
              aria-label={`${isLinked ? 'Unlink' : 'Link'} ${tool.name}`}
              onClick={() => handleToggleToolLink(tool, !isLinked)}
              disabled={isPending}
              className="assistantIconButtonNeutral"
            >
              {isPending
                ? <CircularProgress size={18} />
                : isLinked ? <LinkOffIcon /> : <LinkIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </li>
    );
  };

  return (
    <Box className="assistantSettingsRoot">
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={0} className="assistantSettingsPanel">
        <div className="assistantPanelHead">
          <Typography variant="h6" className="assistantPanelTitle">Linked Tools</Typography>
          {attachedTools.length > 0 && (
            <span className="assistantPanelCount">{attachedTools.length}</span>
          )}
        </div>
        {attachedTools.length === 0 ? (
          <p className="assistantPanelEmpty">
            No tools are linked yet. Link one below to extend what this agent can do.
          </p>
        ) : (
          <ul className="assistantToolList">
            {attachedTools.map((tool) => renderToolRow(tool, true))}
          </ul>
        )}
      </Paper>

      <Paper elevation={0} className="assistantSettingsPanel">
        <div className="assistantPanelHead">
          <Typography variant="h6" className="assistantPanelTitle">Available Tools</Typography>
          {unattachedTools.length > 0 && (
            <span className="assistantPanelCount">{unattachedTools.length}</span>
          )}
        </div>
        {unattachedTools.length === 0 ? (
          <p className="assistantPanelEmpty">
            All available tools are already linked to this agent.
          </p>
        ) : (
          <ul className="assistantToolList">
            {unattachedTools.map((tool) => renderToolRow(tool, false))}
          </ul>
        )}
      </Paper>

      <Box className="assistantHintRow">
        <InfoIcon fontSize="small" />
        <Typography variant="body2">
          Unlinked tools will not be used by the agent.
        </Typography>
      </Box>
    </Box>
  );
};

export default ToolsSettings;
